import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";

export interface CreateAppointmentData {
    doctorId: number;
    appointmentDateTime: string;
    reason: string;
}

export const useAppointmentMutations = () => {
    const queryClient = useQueryClient();

    const createMutation = useMutation({
        mutationFn: async (data: CreateAppointmentData) => {
            const response = await axiosInstance.post(API_ENDPOINTS.APPOINTMENTS.MINE, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['appointments', 'mine'] });
            // Also invalidate stats that might show appointment counts
            queryClient.invalidateQueries({ queryKey: ['doctor', 'stats'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
        },
    });

    const cancelMutation = useMutation({
        mutationFn: async (id: number) => {
            const response = await axiosInstance.post(API_ENDPOINTS.APPOINTMENTS.CANCEL(id));
            return response.data;
        },
        onMutate: async (id: number) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['appointments', 'mine'] });

            // Snapshot the previous value
            const previousAppointments = queryClient.getQueryData<any[]>(['appointments', 'mine']);

            // Optimistically update to the new value
            if (previousAppointments) {
                queryClient.setQueryData(['appointments', 'mine'], (old: any[] | undefined) =>
                    old?.map(app => app.id === id ? { ...app, status: 'CANCELLED' } : app)
                );
            }

            // Return a context object with the snapshotted value
            return { previousAppointments };
        },
        onError: (_err, _id, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousAppointments) {
                queryClient.setQueryData(['appointments', 'mine'], context.previousAppointments);
            }
        },
        onSettled: () => {
            // Always refetch after error or success to synchronize with the server
            queryClient.invalidateQueries({ queryKey: ['appointments', 'mine'] });
        },
    });

    return {
        createAppointment: createMutation,
        cancelAppointment: cancelMutation,
    };
};
